import * as React from 'react';

/**
 * Returns `value` lagged by `delayMs` — re-emits only after the input stops
 * changing for the full delay window. Use to defer side effects (search
 * queries, network fetches, expensive recalculations) until typing settles.
 *
 * ```tsx
 * const debouncedQuery = useDebouncedValue(query, 200);
 * React.useEffect(() => { void search(debouncedQuery); }, [debouncedQuery]);
 * ```
 */
export const useDebouncedValue = <T>(value: T, delayMs: number): T => {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
};

export default useDebouncedValue;
